import React, {useState, useEffect, useRef} from 'react';

import {
    CircularProgress,
    Paper,
    Typography
} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import useStyles from "../styles/styles";
import {useQuery, useMutation} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import SignatureBlockTable from "../components/SignatureBlock/SignatureBlockTable";
import SignatureBlockPairModal from "../components/SignatureBlock/SignatureBlockPairModal";
import useLocalStorage from "../hooks/useLocalStorage";

const SignatureBlocks = () => {
    const classes = useStyles();
    const [success, setSuccess] = useState(false);
    const [modalData, setModalData] = useState({
        modalType: '',
        category: false,
        data: false,
        isPair: false,
        showAddRow: false,
        value: ''
    });
    const [finalValue, setFinalValue] = useState('');
    const closeModal = () => {
        setModalData({
            modalType: '',
            category: false,
            data: false,
            showAddRow: modalData.showAddRow,
            value: ''
        });
    };
    /**
     * SignatureBlock Data GraphQL
     */
    const {loading, error, data, refetch} = useQuery(gql`
        query allSignatureBlocks {
            allSignatureBlock {
                nodes {
                    id
                    dateCreated
                    dateModified
                    blockReason
                    blockValue
                    filterField
                    filterType
                    expires
                    active
                    signatureBlockSource
                }
            }
            allFilterFields: __type(name: "FilterFields"){
                enumValues {
                    name
                }
            }
            allFilterListValueTypes: __type(name: "FilterListValueType"){
                enumValues {
                    name
                }
            }
            allBlockReasons: __type(name: "BlockReason"){
                enumValues {
                    name
                }
            }
            allSignatureBlockSources: __type(name: "SignatureBlockSource"){
                enumValues {
                    name
                }
            }
            allAffiliate {
                nodes {
                    id
                    name
                    key
                }
            }
            allTrafficSource(filter: {
                includeDeleted: true
            }) {
                nodes {
                    id
                    name
                    legacyPromoId
                    minimumPrice
                    affiliate {
                        id
                        name
                    }
                    isDeleted
                }
            }
        }
    `, {
        notifyOnNetworkStatusChange: true,
    });

    /**
     * Create Single Value SignatureBlock Graphql
     */
    const [createSingleValueSignatureBlock, {loading: createSingleValueSignatureBlockLoading, error: createSingleValueSignatureBlockError}] = useMutation(gql`
        mutation CreateSingleValueSignatureBlock(
            $filterField: FilterFields!
            $blockValue: String!
            $expires: DateTime
            $overrideExisting: Boolean!
            $blockReason: BlockReason!
        ) {
            createSingleValueSignatureBlock(
                expires: $expires
                blockValue: $blockValue
                filterField: $filterField
                overrideExisting: $overrideExisting
                blockReason: $blockReason
            ) {
                id
                dateCreated
                dateModified
                blockValue
                blockReason
                filterType
                filterField
                expires
                active
                signatureBlockSource
            }
        }
    `);

    /*
**
* Create Pair Value SignatureBlock Graphql
    */
    const [createPairValueSignatureBlock, {loading: createPairValueSignatureBlockLoading, error: createPairValueSignatureBlockError}] = useMutation(gql`
        mutation CreatePairValueSignatureBlock(
            $filterField: FilterFields!
            $firstBlockValue: String!
            $secondBlockValue: String!
            $expires: DateTime
            $overrideExisting: Boolean!
            $blockReason: BlockReason!
        ) {
            createPairValueSignatureBlock(
                expires: $expires
                firstBlockValue: $firstBlockValue
                secondBlockValue: $secondBlockValue
                filterField: $filterField
                overrideExisting: $overrideExisting
                blockReason: $blockReason
            ) {
                id
                dateCreated
                dateModified
                blockValue
                blockReason
                filterType
                filterField
                expires
                active
                signatureBlockSource
            }
        }
    `);

    /**
     * Update SignatureBlock Graphql
     */
    const [updateSignatureBlock, {loading: updateSignatureBlockLoading, error: updateSignatureBlockError}] = useMutation(gql`
        mutation UpdateSignatureBlock(
            $id: UUID!
            $expires: DateTime
        ) {
            updateSignatureBlock(
                id: $id
                expires: $expires
            ) {
                id
                dateCreated
                dateModified
                blockValue
                blockReason
                filterType
                filterField
                expires
                active
                signatureBlockSource
            }
        }
    `);
    /**
     * Delete SignatureBlock GraphQL
     */
    const [deleteSignatureBlock, {loading: deleteSignatureBlockLoading}] = useMutation(gql`
        mutation DeleteSignatureBlock(
            $id: UUID!
        ) {
            deleteSignatureBlock(id: $id)
        }
    `);
    /**
     * Sync to Prod
     */
    const [updateSignatureBlockFromTest, {loading: syncLoading, data: syncDataResp}] = useMutation(gql`
        mutation syncData {
            updateSignatureBlockFromTest
        }
    `);

    const getError = () => {
        if(error){
            return error.message
        }else if(updateSignatureBlockError){
            return updateSignatureBlockError.message
        }else if(createSingleValueSignatureBlockError){
            return createSingleValueSignatureBlockError.message
        }else if(createPairValueSignatureBlockError){
            return createPairValueSignatureBlockError.message
        }
    }

    useEffect(() => {
        if (!deleteSignatureBlockLoading) {
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteSignatureBlockLoading]);

    useEffect(() => {
        if (!createSingleValueSignatureBlockLoading) {
            closeModal();
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createSingleValueSignatureBlockLoading]);

    useEffect(() => {
        if (!createPairValueSignatureBlockLoading) {
            closeModal();
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createPairValueSignatureBlockLoading]);

    useEffect(() => {
        if (!updateSignatureBlockLoading) {
            closeModal();
            setFinalValue('');
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateSignatureBlockLoading]);

    useEffect(() => {
        if (syncDataResp && syncDataResp.updateSignatureBlockFromTest) {
            setTimeout(() => setSuccess(false), 3000);
            setFinalValue('');
            refetch();
        }
    }, [syncDataResp]);

    /**
     * Main Return for Component
     */
    return (
        <>
            <Typography variant="h2">
                Signature Blocks
            </Typography>
            {!loading && (error || updateSignatureBlockError || createSingleValueSignatureBlockError || createPairValueSignatureBlockError) ? (
                <Paper className={classes.paper}>
                    <Alert severity="error">There is an error!</Alert>
                    <pre>{getError()}</pre>
                </Paper>
            ) : null}
            {loading || syncLoading ? (
                <Paper className={classes.paper}>
                    <div className={classes.loadingBar}>
                        <CircularProgress
                            size={50}
                            thickness={4}
                        />
                    </div>
                </Paper>
            ) : null}
            {!loading && data && data.allSignatureBlock.nodes && !syncLoading ? (
                <>
                    {success ? (
                        <Alert severity="success">Signature Blocks are synced!</Alert>
                    ) : null}
                    <SignatureBlockTable
                        refetch={refetch}
                        data={data}
                        setSuccess={setSuccess}
                        updateSignatureBlockFromTest={updateSignatureBlockFromTest}
                        updateSignatureBlockLoading={updateSignatureBlockLoading}
                        createSingleValueSignatureBlock={createSingleValueSignatureBlock}
                        createPairValueSignatureBlock={createPairValueSignatureBlock}
                        updateSignatureBlock={updateSignatureBlock}
                        deleteSignatureBlock={deleteSignatureBlock}
                        setModalData={setModalData}
                        finalValue={finalValue}
                        setFinalValue={setFinalValue}
                    />
                </>
            ) : null}
            {modalData.isPair ? (
                <SignatureBlockPairModal
                    data={data}
                    modalData={modalData}
                    closeModal={closeModal}
                />
            ) : null}
        </>
    );
};

export default SignatureBlocks;
